import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Calendar,
  History,
  Upload,
  CreditCard,
  Receipt,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

import { HeadphonesIcon } from 'lucide-react';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/app' },
  { icon: Calendar, label: 'Resumo do Dia', path: '/app/daily' },
  { icon: History, label: 'Histórico', path: '/app/history' },
  { icon: Upload, label: 'Importar', path: '/app/import' },
  { icon: CreditCard, label: 'Assinatura', path: '/app/billing' },
  { icon: Receipt, label: 'Faturas', path: '/app/invoices' },
  { icon: Settings, label: 'Configurações', path: '/app/settings' },
  { icon: HeadphonesIcon, label: 'Suporte', path: '/app/support' },
];

export default function AppLayout({ children, sandboxMode = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasCancellationCountdown = user?.cancellation_requested_at && user?.cancellation_effective_at && typeof user?.days_until_access_ends === 'number';
  const accountSegment = user?.account_segment === 'business' ? 'business' : 'personal';
  const segmentLabel = accountSegment === 'business' ? 'BUSINESS' : 'PESSOAL';
  const planLabel =
    user?.plan === 'business'
      ? 'Premium'
      : user?.plan === 'pro'
        ? 'Pro'
        : user?.plan === 'starter'
          ? 'Starter'
          : null;
  const cancellationLabel = hasCancellationCountdown
    ? user.days_until_access_ends <= 0
      ? 'Desativa hoje'
      : `Desativa em ${user.days_until_access_ends} dia(s)`
    : null;

  const isActive = (path) => {
    if (path === '/app') {
      return location.pathname === '/app' || location.pathname.startsWith('/app/groups');
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-zinc-50/50">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-zinc-200 flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-zinc-100">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center mt-0.5">
              <BrandIcon className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <span className="font-heading text-lg font-semibold text-zinc-900 block leading-tight whitespace-nowrap">Zapt Resumo</span>
              <p className="text-[10px] font-semibold tracking-[0.18em] text-sky-700 mt-0.5">
                {segmentLabel}
              </p>
              {planLabel && (
                <div className="mt-1">
                  <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 border border-violet-100">
                    {planLabel}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {sandboxMode && (
            <div className="mt-3">
              <span className="sandbox-badge">SANDBOX MODE</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          {hasCancellationCountdown && (
            <button
              type="button"
              onClick={() => navigate('/app/invoices')}
              className="mb-4 w-full rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left hover:bg-amber-100/70 transition-colors"
            >
              <p className="text-sm font-semibold text-amber-900">{cancellationLabel}</p>
              <p className="text-xs text-amber-700 mt-1">
                Acesso liberado até {new Date(user.cancellation_effective_at).toLocaleDateString('pt-BR')}.
              </p>
            </button>
          )}
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all sidebar-link ${
                    isActive(item.path) ? 'active' : 'text-zinc-600'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-zinc-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-50 transition-colors">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-sky-100 text-sky-700 text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-zinc-900 truncate">{user?.name || 'Usuário'}</p>
                  <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/security')}>
                <Shield className="w-4 h-4 mr-2" />
                Segurança
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-200">
        <div className="flex items-center justify-between px-3 py-3 gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <BrandIcon className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-heading font-semibold text-zinc-900 block leading-tight text-[15px] whitespace-nowrap">Zapt Resumo</span>
              <span className="text-[9px] sm:text-[10px] font-semibold tracking-[0.12em] text-sky-700 block leading-tight">
                {segmentLabel}
              </span>
              {planLabel && (
                <div className="mt-0.5">
                  <span className="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[8px] font-semibold text-violet-700 border border-violet-100 flex-shrink-0">
                    {planLabel}
                  </span>
                </div>
              )}
            </div>
            {sandboxMode && <span className="sandbox-badge text-[8px] px-1.5 py-0.5 flex-shrink-0">SANDBOX</span>}
          </div>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-1.5 rounded-lg hover:bg-zinc-100 flex-shrink-0"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-zinc-200 shadow-lg animate-fade-in">
            <nav className="p-4">
              {hasCancellationCountdown && (
                <button
                  type="button"
                  onClick={() => {
                    navigate('/app/invoices');
                    setMobileMenuOpen(false);
                  }}
                  className="mb-4 w-full rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left"
                >
                  <p className="text-sm font-semibold text-amber-900">{cancellationLabel}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Acesso até {new Date(user.cancellation_effective_at).toLocaleDateString('pt-BR')}
                  </p>
                </button>
              )}
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <button
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        isActive(item.path) ? 'bg-violet-50 text-violet-700' : 'text-zinc-600 hover:bg-zinc-50'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
              
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <button
                  onClick={logout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-5 h-5" />
                  Sair
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 z-50">
        <div className="flex justify-around py-2">
          {navItems.slice(0, 4).map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg ${
                isActive(item.path) ? 'text-sky-600' : 'text-zinc-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0 pb-20 md:pb-0">
        {children}
      </main>
    </div>
  );
}
